import React, { useState } from "react";
import { useSearchParams } from "wouter";
import { flexRender, SortingState, ColumnFiltersState, useTable } from "@tanstack/react-table";
import { cloneDeep } from "lodash";
import { FileSpreadsheet } from "lucide-react";
import { reactTableFeatures } from "./features";

// TypeScript interface for column meta properties (following TanStack Table official approach)
export interface ColumnMeta {
    className?: string;
    // Add other custom meta properties here as needed
}

// Helper function to safely get className from column definition using the official meta property approach
// Note: We use a generic constraint to accept any object that might have meta or className properties
const getColumnClassName = (columnDef: { meta?: ColumnMeta; className?: string; [key: string]: any }): string => {
    return columnDef?.meta?.className || columnDef?.className || "";
};

import { exportToXlsx, sheetType } from "../../src/common/exportToXlsx";
import { MultiSelectDropdown } from "../../src/common/MultiSelectDropdown";
import { DefaultColumnFilter, Filter, resolveFilterFnName } from "./ReactTableFilters";

// https://tanstack.com/table/v9

/**
 * react-table wrapper
 *
 *             <ReactTable
 *              columns={columns}
 *              data={transactions}
 *              defaultHiddenColumns={["_id", "secondCurrency", "secondSettlementAmount", "secondInstrumentName"]}
 *             />
 *
 *  - 'defaultHiddenColumns' is optional. default value is 'null' - hidden columns select is not visible
 *  - use 'defaultHiddenColumns={[]}' when hidden columns select is needed and no default hidden columns
 *  - 'exportToXlsxFile' exports the filtered rows to that file name
 *  - 'onExport' shows the same export icon but lets the page do its own export
 */

export interface Props {
    columns: any[];
    data: any;
    defaultHiddenColumns?: any;
    initialPageSize?: number;
    exportToXlsxFile?: string;
    onExport?: () => void;
    highlightRowOnClick?: boolean;
}

const defaultColumn: any = { filter: DefaultColumnFilter, size: 50, minSize: 1, maxSize: 500 };

export const ReactTable = ({
    columns,
    data: dataIn,
    defaultHiddenColumns = null,
    initialPageSize = 60,
    exportToXlsxFile = "",
    onExport,
    highlightRowOnClick = false
}: Props): React.ReactElement => {
    // Issue with filter if first row has any value that is null, see https://github.com/TanStack/table/issues/4919
    const data = React.useMemo(() => {
        const dataNoNullFirstRow = cloneDeep(dataIn);
        if (dataNoNullFirstRow && dataNoNullFirstRow.length) {
            Object.keys(dataNoNullFirstRow[0]).forEach((key) => {
                if (dataNoNullFirstRow[0][key] === null) {
                    dataNoNullFirstRow[0][key] = "";
                }
            });
        }

        return dataNoNullFirstRow;
    }, [dataIn]);

    // Filter components assign columnDef.filterFn while rendering, after the row model runs.
    // Resolve the filterFn from the filter spec up front so array/id columns keep arrIncludes.
    const tableColumns = React.useMemo(
        () =>
            columns.map((c) => {
                if (c.filterFn) {
                    return c;
                }
                const filterFn = resolveFilterFnName(c.filter);
                return filterFn ? { ...c, filterFn } : c;
            }),
        [columns]
    );

    const [queryArgs] = useSearchParams();
    const initialHiddenColumns = defaultHiddenColumns ? defaultHiddenColumns.filter((c) => !queryArgs[c]) : []; // columns in queryArgs should be visible
    const [hiddenColumns, setHiddenColumnsState] = useState(initialHiddenColumns);
    const getColumnId = (column: any): string => column.id ?? column.accessorKey;

    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const columnVisibility = Object.fromEntries(columns.map((x) => [getColumnId(x), !hiddenColumns.includes(getColumnId(x))]));

    const table = useTable({
        features: reactTableFeatures,
        data,
        columns: tableColumns,
        defaultColumn,
        state: {
            sorting,
            columnFilters,
            columnVisibility
        },
        initialState: { pagination: { pageIndex: 0, pageSize: initialPageSize } },
        enableColumnResizing: true,
        columnResizeMode: "onChange",
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters
    });

    const hiddenColumnsDropdownOptions = columns.map((c) => {
        // at least one column must be visible
        const columnId = getColumnId(c);
        return {
            key: columnId,
            text: c.header,
            hidden: hiddenColumns && hiddenColumns.includes(columnId),
            disabled: hiddenColumns && !hiddenColumns.includes(columnId) && columns.length - 2 < hiddenColumns.length
        };
    });

    return (
        <div className="captor-table table-responsive">
            {defaultHiddenColumns !== null || exportToXlsxFile || onExport ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", marginBottom: "0.5rem" }}>
                    {defaultHiddenColumns !== null ? (
                        <MultiSelectDropdown
                            key="hiddenColumnsDropdown"
                            className="captor-table-select"
                            placeholder="Hidden columns"
                            options={hiddenColumnsDropdownOptions}
                            defaultSelectedOptions={hiddenColumnsDropdownOptions.filter((x) => x.hidden).map((c) => c.key)}
                            onOptionSelect={(data) => {
                                setHiddenColumnsState(data);
                            }}
                        ></MultiSelectDropdown>
                    ) : (
                        <div />
                    )}
                    {exportToXlsxFile || onExport ? (
                        <div
                            title="Export to XLSX"
                            style={{ width: "20px", height: "20px", cursor: "pointer" }}
                            onClick={() => {
                                if (onExport) {
                                    onExport();
                                    return;
                                }
                                const currentRecords: sheetType = table.getFilteredRowModel().rows.map((row) => {
                                    const item: any = row.original;
                                    delete item.__typename;
                                    return item;
                                });
                                exportToXlsx([currentRecords], exportToXlsxFile);
                            }}
                        >
                            <FileSpreadsheet className="Excel" />
                        </div>
                    ) : null}
                </div>
            ) : null}

            <table className="table table-striped table-xs" role="table">
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id} className="tr" role="row">
                            {headerGroup.headers.map((header) => {
                                return (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        style={header.getSize() > 0 ? { width: header.getSize(), minWidth: header.getSize() } : undefined}
                                        className={`th ${getColumnClassName(header.column.columnDef)}`}
                                        role="columnheader"
                                    >
                                        {header.column.getCanResize() && (
                                            <div
                                                onMouseDown={header.getResizeHandler()}
                                                className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
                                            ></div>
                                        )}

                                        {header.isPlaceholder ? null : (
                                            <div
                                                title="Toggle SortBy"
                                                {...{
                                                    className: header.column.getCanSort() ? "cursor-pointer select-none" : "",
                                                    onClick: header.column.getToggleSortingHandler()
                                                }}
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {{
                                                    asc: " ▼",
                                                    desc: " ▲"
                                                }[header.column.getIsSorted() as string] ?? null}
                                            </div>
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    ))}

                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={`f${headerGroup.id}`} className="tr" role="row">
                            {headerGroup.headers.map((header: any) => (
                                <th
                                    key={header.id}
                                    colSpan={header.colSpan}
                                    style={{ width: header.getSize(), minWidth: header.getSize() }}
                                    className={`th ${getColumnClassName(header.column.columnDef)}`}
                                    role="columnheader"
                                >
                                    {!header.isPlaceholder && header.column.getCanFilter() ? (
                                        <div>
                                            <Filter column={header.column} table={table} />
                                        </div>
                                    ) : null}

                                    {header.column.getCanResize() && (
                                        <div
                                            onMouseDown={header.getResizeHandler()}
                                            onTouchStart={header.getResizeHandler()}
                                            className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
                                        ></div>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody role="rowgroup">
                    {table.getRowModel().rows.map((row) => (
                        <tr
                            key={row.id}
                            className={`tr${highlightRowOnClick && selectedRowId === row.id ? " captor-table-row-selected" : ""}`}
                            role="row"
                            onClick={
                                highlightRowOnClick
                                    ? () => {
                                          setSelectedRowId((current) => (current === row.id ? null : row.id));
                                      }
                                    : undefined
                            }
                        >
                            {row.getVisibleCells().map((cell) => (
                                <td
                                    key={cell.id}
                                    className={`td ${getColumnClassName(cell.column.columnDef)}`}
                                    role="cell"
                                    style={{ width: cell.column.getSize(), minWidth: cell.column.getSize() }}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="pagination pb-6">
                <div>
                    <button className="button" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()} title="First">
                        &laquo;
                    </button>{" "}
                    <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} title="Previous">
                        &lt;
                    </button>{" "}
                </div>

                <div className="ms-2">
                    <span>
                        Page:{" "}
                        <input
                            type="number"
                            min={1}
                            max={table.getPageCount()}
                            value={table.state.pagination.pageIndex + 1}
                            onChange={(e) => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                table.setPageIndex(page);
                            }}
                            style={{ maxWidth: "70px" }}
                        />{" "}
                        of {table.getPageCount()}
                    </span>
                </div>

                <div className="ms-2">
                    <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} title="Next">
                        &gt;
                    </button>{" "}
                    <button onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()} title="Last">
                        &raquo;
                    </button>
                </div>

                <div className="ms-5">
                    <select
                        value={table.state.pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                    >
                        {(() => {
                            const defaultPageSizes = [20, 40, 60, 80, 100, 500, 1000];
                            const currentPageSize = table.state.pagination.pageSize;
                            const pageSizes = defaultPageSizes.includes(currentPageSize)
                                ? defaultPageSizes
                                : [...defaultPageSizes, currentPageSize].sort((a, b) => a - b);
                            return pageSizes.map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    Show {pageSize}
                                </option>
                            ));
                        })()}
                    </select>
                </div>
            </div>
        </div>
    );
};
